import {
  StateroomType,
  StateroomSpec,
  LowBerthSpec,
  EmergencyLowBerthSpec,
  OccupancyType,
  CommonAreaSpec,
} from "../types/stateroom";

// Stateroom types and specifications
export const STATEROOM_TYPES: Record<StateroomType, StateroomSpec> = {
  STANDARD: {
    type: "STANDARD",
    name: "Standard Stateroom",
    description: "Basic accommodation for one person",
    tons: 4,
    cost: 500000, // MCr0.5
    power: 0,
    techLevel: 7,
    capacity: 1,
    maintenanceRatio: 0.01, // 1% of cost per year
    comfort: 1.0,
    special: null,
  },
  LUXURY: {
    type: "LUXURY",
    name: "Luxury Stateroom",
    description: "High-end accommodation with additional amenities",
    tons: 6,
    cost: 1000000, // MCr1
    power: 1,
    techLevel: 8,
    capacity: 1,
    maintenanceRatio: 0.02, // 2% of cost per year
    comfort: 1.5,
    special: "+1 to passenger morale",
  },
  SUITE: {
    type: "SUITE",
    name: "Suite",
    description: "Luxurious multi-room accommodation",
    tons: 8,
    cost: 2000000, // MCr2
    power: 2,
    techLevel: 9,
    capacity: 2,
    maintenanceRatio: 0.025, // 2.5% of cost per year
    comfort: 2.0,
    special: "+2 to passenger morale",
  },
} as const;

export const STATEROOM = STATEROOM_TYPES;

// Low berth specifications
export const LOW_BERTH_SPEC: LowBerthSpec = {
  type: "LOW_BERTH",
  name: "Low Berth",
  description: "Cryogenic suspension berth for long-term storage",
  count: 0,
  cost: 50000, // Cr50,000
  tons: 0.5,
  power: 1, // 1 Power for every 10 berths or portion thereof
  capacity: 1,
  techLevel: 7,
  maintenanceCost: 1000, // Cr1,000 per year
  special: "Cryogenic suspension",
  failureRate: 0.01, // 1% chance of failure per year
} as const;

// Emergency low berth specifications
export const EMERGENCY_LOW_BERTH_SPEC: EmergencyLowBerthSpec = {
  type: "EMERGENCY_LOW_BERTH",
  name: "Emergency Low Berth",
  description: "High-reliability cryogenic suspension berth",
  count: 0,
  cost: 1000000, // MCr1
  tons: 1,
  power: 1,
  capacity: 4,
  techLevel: 8,
  maintenanceCost: 5000, // Cr5,000 per year
  special: "Emergency cryogenic suspension",
  failureRate: 0.001, // 0.1% chance of failure per year
} as const;

// Common area specifications
export const COMMON_AREA_SPEC: CommonAreaSpec = {
  costPerTon: 100000, // Cr100,000 per ton
  recommendedRatio: 0.25, // 25% of stateroom space
  minimumRatio: 0.1, // Minimum 10% of stateroom space
  maximumRatio: 0.5, // Maximum 50% of stateroom space
  comfortBonus: 0.2, // +20% to overall comfort
  powerPerTon: 0.5,
  maintenanceRatio: 0.015, // 1.5% of cost per year
} as const;

// Occupancy types
export const OCCUPANCY_TYPES: Record<
  OccupancyType,
  {
    name: string;
    comfortModifier: number;
    description: string;
  }
> = {
  single: {
    name: "Single Occupancy",
    comfortModifier: 1.0,
    description: "One person per stateroom",
  },
  double: {
    name: "Double Occupancy",
    comfortModifier: 0.8,
    description: "Two people per stateroom",
  },
} as const;

// Helper functions
export const getStateroomSpec = (type: StateroomType): StateroomSpec => {
  return STATEROOM_TYPES[type];
};

export const calculateStateroomCost = (
  type: StateroomType,
  count: number,
): number => {
  const spec = getStateroomSpec(type);
  return spec.cost * count;
};

export const calculateStateroomTons = (
  type: StateroomType,
  count: number,
): number => {
  const spec = getStateroomSpec(type);
  return spec.tons * count;
};

export const calculateLowBerthPower = (count: number): number => {
  return Math.ceil(count / 10); // 1 power per 10 berths, rounded up
};

export const calculateCommonAreaTons = (
  totalStateroomTons: number,
  ratio: number,
): number => {
  const spec = COMMON_AREA_SPEC;
  const actualRatio = Math.max(
    spec.minimumRatio,
    Math.min(spec.maximumRatio, ratio),
  );
  return totalStateroomTons * actualRatio;
};

// Validation functions
export const validateStateroomSpec = (spec: StateroomSpec): string[] => {
  const errors: string[] = [];

  if (!spec.type) {
    errors.push("Stateroom type is required");
  }

  if (!spec.name) {
    errors.push("Stateroom name is required");
  }

  if (spec.tons <= 0) {
    errors.push("Tons must be greater than 0");
  }

  if (spec.cost < 0) {
    errors.push("Cost cannot be negative");
  }

  if (spec.power < 0) {
    errors.push("Power requirement cannot be negative");
  }

  if (spec.techLevel < 0) {
    errors.push("Tech level cannot be negative");
  }

  if (spec.capacity <= 0) {
    errors.push("Capacity must be greater than 0");
  }

  if (spec.maintenanceRatio < 0) {
    errors.push("Maintenance ratio cannot be negative");
  }

  if (spec.comfort <= 0) {
    errors.push("Comfort rating must be greater than 0");
  }

  return errors;
};

export const validateStateroomConfiguration = (
  stateroomCount: number,
  lowBerthCount: number,
  emergencyLowBerthCount: number,
  commonAreaRatio: number,
): string[] => {
  const errors: string[] = [];

  if (stateroomCount < 0) {
    errors.push("Stateroom count cannot be negative");
  }

  if (lowBerthCount < 0) {
    errors.push("Low berth count cannot be negative");
  }

  if (emergencyLowBerthCount < 0) {
    errors.push("Emergency low berth count cannot be negative");
  }

  if (commonAreaRatio < COMMON_AREA_SPEC.minimumRatio) {
    errors.push(
      `Common area ratio must be at least ${COMMON_AREA_SPEC.minimumRatio * 100}%`,
    );
  }

  if (commonAreaRatio > COMMON_AREA_SPEC.maximumRatio) {
    errors.push(
      `Common area ratio cannot exceed ${COMMON_AREA_SPEC.maximumRatio * 100}%`,
    );
  }

  return errors;
};

export const LOW_BERTH = LOW_BERTH_SPEC;
export const EMERGENCY_LOW_BERTH = EMERGENCY_LOW_BERTH_SPEC;
export const COMMON_AREA = COMMON_AREA_SPEC;
